import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { daysheetAPI, userAPI } from '../services/api';
import Layout from '../components/Layout';
import './Daysheet.css';

const Icon = ({ name, size = 20 }) => {
  const icons = {
    calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
    mapPin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
  };
  return icons[name] || null;
};

const Daysheet = () => {
  const navigate = useNavigate();
  const [daysheets, setDaysheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    role: '',
    employee: ''
  });

  const user = JSON.parse(localStorage.getItem('user'));
  const isManager = ['HR', 'Operations Head', 'Project Manager', 'Project Lead'].includes(user?.role);

  useEffect(() => {
    fetchDaysheets();
    if (isManager) fetchTeamMembers();
  }, []);

  const fetchDaysheets = async (currentFilters = filters) => {
    setLoading(true);
    try {
      const response = isManager
        ? await daysheetAPI.getAll(currentFilters)
        : await daysheetAPI.getMyDaysheets(currentFilters);
      setDaysheets(response.data.data);
    } catch (error) {
      console.error('Failed to fetch daysheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await userAPI.getAll();
      setTeamMembers(res.data.data);
    } catch (err) {
      console.error('Failed to fetch team members', err);
    }
  }

  const handleFilterChange = (e) => {
    const newFilters = { ...filters, [e.target.name]: e.target.value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    fetchDaysheets(filters);
  };

  const resetFilters = () => {
    const emptyFilters = { startDate: '', endDate: '', role: '', employee: '' };
    setFilters(emptyFilters);
    fetchDaysheets(emptyFilters);
  };

  return (
    <Layout>
      <div className="marsh-daysheet-page">
        <header className="marsh-page-header">
          <div className="header-left">
            <span className="marsh-chip">Tracking & Visibility</span>
            <h1 className="minimal-title">{isManager ? 'Team Daysheets' : 'My Daysheets'}</h1>
            <p className="minimal-subtitle">Daily work summaries, blockers, and progress insights</p>
          </div>
          {isManager && (
            <div className="header-stats">
              <div className="mini-stat">
                <span className="label">Submitted Today</span>
                <span className="val">{daysheets.filter(d => new Date(d.date).toDateString() === new Date().toDateString()).length}</span>
              </div>
            </div>
          )}
        </header>

        <div className="marsh-filters-board">
          <div className="filter-grid">
            <div className="marsh-input-group">
              <label>From Date</label>
              <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
            </div>
            <div className="marsh-input-group">
              <label>To Date</label>
              <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
            </div>
            {isManager && (
              <>
                <div className="marsh-input-group">
                  <label>Role</label>
                  <select name="role" value={filters.role} onChange={handleFilterChange}>
                    <option value="">All Roles</option>
                    <option value="Developer">Developer</option>
                    <option value="QA Engineer">QA Engineer</option>
                    <option value="UI Engineer">UI Engineer</option>
                    <option value="Project Lead">Project Lead</option>
                  </select>
                </div>
                <div className="marsh-input-group">
                  <label>Employee</label>
                  <select name="employee" value={filters.employee} onChange={handleFilterChange}>
                    <option value="">Everyone</option>
                    {teamMembers.map(m => (
                      <option key={m._id} value={m._id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="filter-actions">
            <button onClick={applyFilters} className="marsh-btn-primary">Apply Filters</button>
            <button onClick={resetFilters} className="marsh-btn-ghost">Reset</button>
          </div>
        </div>

        {loading ? (
          <div className="marsh-loader">
            <div className="marsh-spinner"></div>
            <p>Optimizing your view...</p>
          </div>
        ) : daysheets.length === 0 ? (
          <div className="marsh-empty-slate">
            <div className="empty-artwork">
              <Icon name="calendar" size={48} />
            </div>
            <h3>No daysheets recorded</h3>
            <p>We couldn't find any daysheet entries for the selected period.</p>
          </div>
        ) : (
          <div className={`marsh-daysheets-list-wrapper ${isManager ? 'is-manager-view' : 'is-user-view'}`}>
            <div className="list-container">
              <div className="list-header-row">
                <div className="col">Date</div>
                {isManager && <div className="col">Employee</div>}
                <div className="col">Hours</div>
                <div className="col">Location</div>
                <div className="col summary-col">Work Summary</div>
                <div className="col">Status</div>
                <div className="col text-right">Action</div>
              </div>
              {daysheets.map((daysheet) => (
                <div key={daysheet._id} className="marsh-daysheet-row" onClick={() => navigate(`/daysheet/${daysheet._id}`)}>
                  <div className="col row-date">
                    <span className="day">{new Date(daysheet.date).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                    <span className="date">{new Date(daysheet.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>

                  {isManager && (
                    <div className="col row-user">
                      <div className="avatar-mini">{daysheet.userName?.charAt(0)}</div>
                      <div className="user-text">
                        <span className="name">{daysheet.userName}</span>
                        <span className="role">{daysheet.role}</span>
                      </div>
                    </div>
                  )}

                  <div className="col row-metrics">
                    <div className="metric">
                      <Icon name="clock" size={14} />
                      <span>{daysheet.totalWorkingHours.toFixed(1)}h</span>
                    </div>
                  </div>

                  <div className="col row-location">
                    <div className="metric">
                      <Icon name="mapPin" size={14} />
                      <span>{daysheet.workLocation}</span>
                    </div>
                  </div>

                  <div className="col row-summary">
                    <p>{daysheet.workSummary.substring(0, 70)}{daysheet.workSummary.length > 70 ? '...' : ''}</p>
                  </div>

                  <div className="col row-status">
                    <span className={`marsh-status-badge ${daysheet.status.toLowerCase()}`}>
                      {daysheet.status}
                    </span>
                  </div>

                  <div className="col row-action text-right">
                    <div className="indicator-tags">
                      {daysheet.blockers && <span className="tag blocker" title="Has Blockers"><Icon name="alert" size={12} /></span>}
                      {daysheet.tasksWorkedOn?.length > 0 && <span className="tag tasks" title={`${daysheet.tasksWorkedOn.length} tasks`}><Icon name="check" size={12} /></span>}
                    </div>
                    <button className="view-details-btn" onClick={(e) => { e.stopPropagation(); navigate(`/daysheet/${daysheet._id}`); }}>
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Daysheet;
